import { isSetKitProduct } from '~/types/product/models'

export function getSectionKey(item: any): string {
  if (item._selectUid) {
    const path = item._selectUid.split('__')[1]
    return `${item.supplierProduct?.id || ''}__${path}`
  }
  return (
    item.supplierProduct?.id ||
    item.parentProduct?.id ||
    (item.label || '').split('|')[0].trim()
  )
}

export function isFromSet(item: any): boolean {
  if (item._isFromSet) {
    return true
  }
  return (
    item.supplierProduct?.product &&
    isSetKitProduct(item.supplierProduct.product)
  )
}

/**
 * The two Set flags bound a section's pick count: `doAllowNoSelectionFromSet`
 * lowers the minimum from one to zero, `doAllowMultipleSelectionsFromSet` lifts
 * the maximum from one to unbounded. They are independent, so a Set can be
 * exactly-one, zero-or-one, one-or-more, or any subset.
 *
 * Both are read from the OWNING supplier-product when there is one. An option
 * built from the product catalogue alone (the populate*FromKitProduct specs)
 * has no supplier-product, so `getProductKitItemsForKitProductDeep` tags it with
 * the owning kit's flags instead -- the same way it tags `_isFromSet`.
 */
export function allowsNoSelection(item: any): boolean {
  if (item.supplierProduct) {
    return !!item.supplierProduct.doAllowNoSelectionFromSet
  }
  return !!item._doAllowNoSelectionFromSet
}

export function allowsMultipleSelections(item: any): boolean {
  if (item.supplierProduct) {
    return !!item.supplierProduct.doAllowMultipleSelectionsFromSet
  }
  return !!item._doAllowMultipleSelectionsFromSet
}

/**
 * Vuetify 2 marked a separator row with a bare `divider: true`; Vuetify 3 keys it off
 * `item.type` instead. services/products.ts, suppliers.ts and supplierProducts.ts now emit
 * the V3 shape, so matching only the old key let dividers through as real items.
 * Both shapes are accepted because getSelectList still spreads the source item.
 */
export function isDivider(item: any): boolean {
  return !!item?.divider || item?.type === 'divider'
}

/** The key a pick is stored and matched by (optionSections' getOptionUid). */
function getUid(item: any): string {
  return item?._selectUid ?? item?.id
}

/**
 * Option uid -> the uid of the option it depends on, for every option whose
 * parent is itself on offer in `items`.
 *
 * ⚠ A SET INSIDE A SET. When one alternative of a Set is itself a Set, that
 * inner Set's options apply only while it is the alternative taken; each one
 * carries the outer option's uid as `_parentOptionUid` (set by both options
 * sources). A parent that is NOT among `items` -- a dialog scoped to the inner
 * Set alone -- imposes nothing, so the inner Set stands on its own there.
 */
export function getParentOptionUidByUid(
  items: any[] | null | undefined
): Map<string, string> {
  const options = (items || []).filter((item) => item && !isDivider(item))
  const offered = new Set(options.map(getUid))
  const parentByUid = new Map<string, string>()
  for (const option of options) {
    const parentUid = option._parentOptionUid
    if (parentUid && offered.has(parentUid)) {
      parentByUid.set(getUid(option), parentUid)
    }
  }
  return parentByUid
}

/**
 * The picks, less every one that depends on an option not picked -- however
 * deep. Taking an outer Set's other alternative leaves the inner Set's picks
 * meaningless, and they must not be built.
 */
export function dropPicksUnderUnpickedOptions(
  items: any[] | null | undefined,
  picks: any[] | null | undefined
): any[] {
  const parentByUid = getParentOptionUidByUid(items)
  let kept = picks || []
  for (;;) {
    const keptUids = new Set(kept.map(getUid))
    const next = kept.filter((pick) => {
      const parentUid = parentByUid.get(getUid(pick))
      return !parentUid || keptUids.has(parentUid)
    })
    if (next.length === kept.length) {
      return next
    }
    kept = next
  }
}

/**
 * Is this item one of a Set's MUTUALLY EXCLUSIVE alternatives -- a Set that
 * permits at most one pick? A Set that allows multiple selections is not.
 */
function isFromExclusiveSet(item: any): boolean {
  return isFromSet(item) && !allowsMultipleSelections(item)
}

/**
 * The UPPER bound: in each exclusive Set, keep only the latest pick. Sections
 * that allow multiple selections, and plain-kit add-ons, pass through untouched.
 * (The name predates `doAllowMultipleSelectionsFromSet`; it is kept because
 * every create-time spec and the options dialog call it.)
 */
export function enforceOnePerSection(value: any[]): any[] {
  if (!value || value.length === 0) {
    return value
  }
  const lastPerSection = new Map<string, number>()
  value.forEach((item, i) => {
    if (isFromExclusiveSet(item)) {
      lastPerSection.set(getSectionKey(item), i)
    }
  })
  return value.filter((_item, i) => {
    const item = value[i]
    if (!isFromExclusiveSet(item)) {
      return true
    }
    const key = getSectionKey(item)
    return lastPerSection.get(key) === i
  })
}

/**
 * The LOWER bound: every Set needs a pick unless it allows none. Says "at least
 * one" for a Set that allows multiple selections, since "one" would read as a
 * ceiling the user is not actually held to.
 */
export function validateOnePerSection(
  value: any[] | null,
  items: any[],
  fieldLabel: string
): true | string {
  const realItems = (items || []).filter(
    (i: any) => !isDivider(i) && isFromSet(i)
  )
  const allSections = new Set(realItems.map(getSectionKey))
  if (allSections.size === 0) {
    return true
  }
  // A nested Set whose outer alternative was not taken asks for nothing.
  const parentByUid = getParentOptionUidByUid(items)
  const picks = dropPicksUnderUnpickedOptions(items, value)
  const pickedUids = new Set(picks.map(getUid))
  const skippableSections = new Set(
    realItems
      .filter((i: any) => {
        const parentUid = parentByUid.get(getUid(i))
        return (
          allowsNoSelection(i) || (!!parentUid && !pickedUids.has(parentUid))
        )
      })
      .map(getSectionKey)
  )
  const selectedSections = new Set(picks.map(getSectionKey))
  const missing = Array.from(allSections).filter(
    (s) => !selectedSections.has(s) && !skippableSections.has(s)
  )
  if (missing.length > 0) {
    const idToDescr = new Map(
      realItems.map((i: any) => [
        getSectionKey(i),
        // Each section's select is titled `_optionSectionDescr` when the
        // source sets one (cynqueModules/optionSections.ts), so name a missing
        // section the way the user sees it.
        i._optionSectionDescr ||
          i.supplierProduct?.descr ||
          i.supplierProduct?.product?.descr ||
          i.parentProduct?.descr ||
          i.parentProduct?.label ||
          getSectionKey(i),
      ])
    )
    const multipleSections = new Set(
      realItems
        .filter((i: any) => allowsMultipleSelections(i))
        .map(getSectionKey)
    )
    const describe = (sections: string[]) =>
      sections.map((id) => idToDescr.get(id) || id).join(', ')
    const missingOne = missing.filter((s) => !multipleSections.has(s))
    const missingAtLeastOne = missing.filter((s) => multipleSections.has(s))
    const messages: string[] = []
    if (missingOne.length > 0) {
      messages.push(`Select one option from: ${describe(missingOne)}`)
    }
    if (missingAtLeastOne.length > 0) {
      messages.push(
        `Select at least one option from: ${describe(missingAtLeastOne)}`
      )
    }
    return messages.join('. ')
  }
  return true
}
